import React, { useState } from "react";
import BackButton from "../shared/backButton";
import appConstants from "../shared/config/constants.json";

import "./LegalPage.css";

const TermsAndConditions = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const { MAYAMAYA_PROD_WHITELABEL_SUPPORT_URL } = appConstants;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();

    // open the section
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: true,
    }));

    // scroll to the section
    const el = document.getElementById(sectionId);
    if (el) {
      const yOffset = -80; // adjust offset here
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = "/Terms-And-Conditions.pdf";
    link.download = "Terms-And-Conditions.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBack = () => {
    window.history.back();
  };

  const sections = [
    {
      id: "account",
      title: "Your Account, Account Registration, and Security",
    },
    { id: "billing", title: "Subscription and Billing" },
    { id: "access", title: "Access, Profile, & Usage" },
    { id: "conduct", title: "Code of Conduct for Users" },
    {
      id: "advertising",
      title: "Advertising, Sponsorship, and Promotional Emails",
    },
    { id: "disclosure", title: "Disclosure of Your Profile" },
    { id: "ip", title: "About Intellectual Property Rights" },
    { id: "counselors", title: "Messages to MayaMaya Counselors" },
    {
      id: "reliance",
      title: "Reliance on MayaMaya's Assessments and Recommendations",
    },
    { id: "privacy", title: "Your Privacy" },
    {
      id: "liability",
      title:
        "Our Liability, Disclaimer Of Warranty, And Limitations Of Liability",
    },
    { id: "indemnification", title: "Indemnification" },
    { id: "miscellaneous", title: "Miscellaneous" },
  ];

  return (
    <div className="legal-wrapper">
      <div className="legal-main-content">
        <BackButton />

        {/* Header Section */}
        <div className="legal-hero-section">
          <div className="legal-hero-inner">
            <h1 className="legal-hero-title">Terms & Conditions</h1>
            <p className="legal-hero-updated">Last Updated: March 2026</p>
            {/* <button className="legal-print-button" onClick={handleDownload}>
              Download as PDF
            </button> */}
          </div>
        </div>

        {/* Acceptance Notice */}
        <div className="legal-container">
          <div className="legal-acceptance-notice">
            <h2 className="legal-acceptance-title">Welcome to MayaMaya</h2>
            <p className="legal-acceptance-text">
              Here, you can elevate your value. MayaMaya is owned and operated
              by Catenate Corp. ("Company" or "we"). The following Terms &
              Conditions, together with our Privacy Policy (collectively, "Terms
              & Conditions") govern your access to and use of the Company's
              mobile application, tablet application, technology platform, and
              website including any content, functionality, and services offered
              on or through such application, website, or platform
              (collectively, "MayaMaya").
            </p>
            <p className="legal-acceptance-text">
              Please read the Terms & Conditions carefully before you start to
              use MayaMaya. By clicking to "accept" or "agree" to the Terms &
              Conditions, you represent that you are at least of Minimum Age.
              "Minimum Age" means the age at which you are able to lawfully
              enter into a binding contract without parental/guardian consent.
              Generally the Minimum Age is 13 years old, but your local laws may
              define the age to be older.
            </p>
            <p className="legal-acceptance-text">
              When you check your acceptance of the Terms & Conditions, this
              creates a binding legal agreement between you and the Company. If
              you find these Terms & Conditions to be unacceptable, you must
              immediately terminate your use of MayaMaya.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="legal-toc-section">
            <h2 className="legal-toc-title">Quick Navigation</h2>
            <div className="legal-toc-grid">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="legal-toc-link"
                  onClick={(e) => handleNavClick(e, section.id)}
                >
                  {index + 1}. {section.title}
                </a>
              ))}
            </div>
          </div>

          {/* Updates Notice */}
          <div className="legal-info-box">
            <h3>Important Notice About Updates</h3>
            <p>
              The Company may update these Terms & Conditions at any time upon
              prior written notice (typically by email). Your continued use of
              MayaMaya after our written notice signals your acceptance to
              continue to be bound by these Terms & Conditions. If you find
              these updated Terms & Conditions to be unacceptable, you may not
              use MayaMaya.
            </p>
          </div>

          {/* Main Sections */}
          <section id="account" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("account")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["account"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                1. Your Account, Account Registration (or Deactivation), and
                Security
              </h2>
            </button>
            {expandedSections["account"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>1.1 Account Creation Methods</h3>
                  <p>
                    There are two ways to create an account with us. You may
                    create an account directly with us or you may access
                    MayaMaya through a third-party (e.g. your employer; a
                    recruiter). Your account will always belong to you. However,
                    if you were provided access to MayaMaya through a
                    third-party, the party paying for MayaMaya has the right to
                    control your access, receive your data, access your account,
                    and get reports on your account (including your assessment
                    and use).
                  </p>
                  <p>
                    NOTE: If you allow a third-party app or site such as
                    Facebook or LinkedIn to authenticate you or pre-populate
                    your profile during the Account registration process, then
                    such third party app or site has its own legal terms and
                    privacy policy, and you may be granting others permission to
                    use your account information in ways we would not. Neither
                    MayaMaya nor the Company is responsible for these other apps
                    and sites.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>1.2 Username Requirements</h3>
                  <p>
                    You understand that you may not (i) select or use a name of
                    another person with the intent to impersonate that person;
                    or (ii) use a name that we, in our sole discretion, deem
                    offensive.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>1.3 Single Account Per User</h3>
                  <p>
                    You agree not to create more than one account with MayaMaya.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>1.4 Registration Information</h3>
                  <p>
                    As part of the registration process, you will be asked for
                    certain information such as your full name, email address,
                    location, and password. You may also be asked questions
                    pertaining to your education, profession, and/or your
                    desired professional and educational goals. It is a
                    condition of your use of MayaMaya that all the information
                    you provide on MayaMaya is correct, current, and complete.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>1.5 Password Confidentiality</h3>
                  <p>
                    You will be responsible for maintaining the confidentiality
                    of your password. You never will be required to reveal your
                    password to any representative or agent of MayaMaya, its
                    owners or agents.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>1.6 Privacy Policy Consent</h3>
                  <p>
                    You agree that all information you provide to register with
                    MayaMaya is governed by our Privacy Policy, and you consent
                    to all actions we take with respect to your information
                    consistent with our Privacy Policy.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>1.7 Third-Party Registration</h3>
                  <p>
                    Registration done through third-party sources such as Apple
                    and Google will be governed by their authentication
                    policies.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>1.8 Security Notification</h3>
                  <p>
                    You shall notify{" "}
                    <a href={MAYAMAYA_PROD_WHITELABEL_SUPPORT_URL}>
                      <strong>MayaMaya Support</strong>
                    </a>{" "}
                    of any known or suspected unauthorized use(s) of your
                    subscription, or any known or suspected breach of security,
                    including loss, theft, or unauthorized disclosure of your
                    password or account information.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>1.9 Account Deactivation</h3>
                  <p>
                    You may request to deactivate/cancel your account at any
                    time by contacting{" "}
                    <a href={MAYAMAYA_PROD_WHITELABEL_SUPPORT_URL}>
                      MayaMaya Support
                    </a>
                    .
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>1.10 Account Suspension</h3>
                  <p>
                    We have the right to disable any user-name, password, or
                    other identifier at any time in our sole discretion for any
                    or no reason, including if, in our opinion, you have
                    violated any provision of these Terms & Conditions.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="billing" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("billing")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["billing"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                2. Subscription and Billing
              </h2>
            </button>
            {expandedSections["billing"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>2.1 Pricing Variations</h3>
                  <p>
                    You acknowledge that you may pay more or less than other
                    users based on factors such as, but not limited to,
                    applicable taxes and foreign exchange rates. We may
                    calculate applicable taxes and fees based on the billing
                    information you provide us at the time of purchasing a
                    subscription agreement rather than the address you provide
                    us as your residential address.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>2.2 Payment Agreement</h3>
                  <p>
                    When you purchase a subscription from us, you agree to pay
                    us applicable fees and taxes.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>2.3 Payment Method Authorization</h3>
                  <p>
                    You grant us authorization to store and bill your payment
                    method. Your payment method may be automatically changed at
                    the beginning of each subscription period.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="access" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("access")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["access"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                3. Access, Profile, & Usage
              </h2>
            </button>
            {expandedSections["access"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>3.1 Service Availability</h3>
                  <p>
                    We reserve the right to withdraw or amend MayaMaya in our
                    sole discretion without notice. We will not be liable if for
                    any reason all or any parts of MayaMaya are unavailable at
                    any time or for any period. From time to time, we may
                    restrict access to some parts of MayaMaya, to users,
                    including registered users.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>3.2 Personal Use Restriction</h3>
                  <p>
                    Unless you are a corporate entity or a business purchasing
                    MayaMaya for your professional needs, MayaMaya may only be
                    used for personal use.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>3.3 Corporate/Business Use</h3>
                  <p>
                    If you are a corporate entity or a business, you may use
                    MayaMaya for your internal business needs only. As a
                    corporate entity or business, you acknowledge that you will
                    limit access to MayaMaya to only your employees, prospective
                    candidates, and independent contractors, each of whom are
                    also subject to these Terms & Conditions as an authorized
                    user.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>3.4 Profile and Analytics Data</h3>
                  <p>
                    For certain features and services on MayaMaya, you will be
                    presented with a set of questions (e.g. in the form of a
                    quiz; self-assessments, etc.) that we will use to develop a
                    Profile about you. You acknowledge that we have a right to
                    collect and retain your data and use your data to develop
                    your Profile. Data that we collect refers to information we
                    collect from you, including your responses of any questions,
                    input, goals, objectives, self-assessments, analytics and
                    real-time user interaction (collectively, your "Analytics").
                    You agree that you will provide true, accurate, current, and
                    complete information when answering our questions. You
                    acknowledge and agree to take the set of questions
                    seriously.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>3.5 Profile Generation</h3>
                  <p>
                    After answering questions and/or based on your own
                    self-assessment, MayaMaya will review your Analytics and
                    generate a profile specific to you comprising in part on
                    your Analytics ("Profile"). The Profile contains MayaMaya's
                    assessment on aptitude and soft skills (including
                    communication, adaptability, leadership, teamwork, etc.)
                    based on proprietary know-how, technology, methodology,
                    software, and trade secrets.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>3.6 Recommendations</h3>
                  <p>
                    You acknowledge and agree that based on your Profile, we may
                    recommend courses, institutions, and careers that purport to
                    align with your personality, aptitude, education, goals,
                    and/or objections. You understand that while we make a bona
                    fide attempt to make recommendations based on your Profile,
                    there is no guarantee that such recommendations are suitable
                    for you.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>3.7 No Guarantees</h3>
                  <p>
                    We make no representation, warranty, guarantee that
                    MayaMaya, or any course of action we recommend will enhance
                    your education, earnings and/or earning potential.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>3.8 Profile Sharing</h3>
                  <p>
                    You acknowledge that we may share your Profile with your
                    employer, prospective employer, recruiter, independent
                    contracting party, college, universities, and/or schools. If
                    your account is through your employer or a recruiter, such
                    Profile may be automatically assessable to them despite your
                    privacy settings.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="conduct" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("conduct")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["conduct"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                4. Code of Conduct for Users
              </h2>
            </button>
            {expandedSections["conduct"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>4.1 Non-Acceptable and Prohibited Uses</h3>
                  <p>
                    Incidents and uses that may cause your account to be
                    terminated include, but are not limited to:
                  </p>
                  <ul className="legal-content-list">
                    <li>Using or attempting to use another user's account</li>
                    <li>
                      Uploading or transmission of any trojans, worms, viruses
                      or any code of a destructive or spying nature
                    </li>
                    <li>
                      Removing any proprietary notices or label on any Content,
                      or using MayaMaya in conjunction with any device or
                      service designed to circumvent technological measures
                      employed to control access to, or the rights in, any
                      Content or other work protected under copyright laws
                    </li>
                    <li>
                      Uploading of transmission of any file or content that harm
                      minors in any way, including any form of child pornography
                    </li>
                    <li>
                      Uploading or transmission of any file or content that
                      spreads messages of terror or depicts torture or
                      death-images
                    </li>
                    <li>
                      Uploading or transmission of files intended to threaten or
                      harass any individual or organization
                    </li>
                    <li>
                      Using MayaMaya for any purpose other than which it is
                      intended
                    </li>
                    <li>
                      Using MayaMaya for submission, generation or inclusion of
                      unsolicited bulk email or other forms of email abuse (i.e.
                      SPAM)
                    </li>
                    <li>
                      Using MayaMaya for advertising, trading of goods or
                      services, or other commercial use except as permitted by
                      MayaMaya
                    </li>
                    <li>Attempting to impersonate any person</li>
                    <li>
                      Attempting to or actually using, disclosing, or
                      distributing any data pertaining to a user profile in
                      violation of these Terms and Conditions
                    </li>
                    <li>Using identifying information in a defamatory way</li>
                    <li>
                      Uploading any programs that may cause a disruption to
                      MayaMaya
                    </li>
                    <li>
                      Taking any action that violates the laws of applicable
                      local, state, federal or international governmental bodies
                    </li>
                    <li>
                      Sharing the account with anyone or re-selling MayaMaya
                      without express written permission from MayaMaya
                    </li>
                    <li>
                      Using bots or other automated methods to access MayaMaya
                    </li>
                    <li>
                      Using MayaMaya for a non-bona fide purpose or for
                      intentionally making any misrepresentations (whether about
                      yourself, your employer, a job posting, or otherwise)
                    </li>
                  </ul>
                </div>

                <div className="legal-subsection">
                  <h3>4.2 Monitoring and Cooperation</h3>
                  <p>
                    The Company will cooperate with any and all appropriate
                    legal authorities in investigating claims of illegal
                    activity, including, but not limited to illegal transfer or
                    use of copyrighted material or other illegal activity.
                    MayaMaya reserves the right to monitor or view material
                    uploaded by users onto MayaMaya at any time for the purpose
                    of ensuring compliance with this Agreement.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="advertising" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("advertising")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["advertising"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                5. Advertising, Sponsorship, and Promotional Emails
              </h2>
            </button>
            {expandedSections["advertising"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>5.1 Third-Party Advertising</h3>
                  <p>
                    You agree that we and our third-party providers and partners
                    may place advertising on MayaMaya.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>5.2 Sponsored Recommendations</h3>
                  <p>
                    You acknowledge and agree that some of our recommendations
                    may feature our sponsors. Our sponsors are those persons
                    with whom we have exchanged consideration for value such as
                    money or reciprocal promotion of platforms.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>5.3 Promotional Communications</h3>
                  <p>
                    MayaMaya may send promotional emails to notify you of
                    upgrades and new features. At any time you may elect to
                    opt-out of these e-mails or notifications based on your
                    systems and device functionalities. MayaMaya also may use
                    third party platforms such as Facebook, Snapchat, TikTok,
                    YouTube, and Twitter to send advertising to Members who use
                    those respective social networks.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="disclosure" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("disclosure")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["disclosure"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                6. Disclosure of Your Profile
              </h2>
            </button>
            {expandedSections["disclosure"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>6.1 Profile Visibility</h3>
                  <p>
                    You acknowledge and agree that all or part of your account,
                    including your Profile, may be visible to third parties such
                    as recruiters, prospective and employers depending on your
                    form of subscription.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>6.2 Employer/Recruiter Access</h3>
                  <p>
                    If you were requested to create an account by your employer,
                    prospective employer, or recruiter, then all or parts of
                    your Profile may be visible to such persons, irrespective of
                    whether you opted to disclose your Profile to third-parties
                    and/or your privacy settings.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>6.3 Third-Party Liability Disclaimer</h3>
                  <p>
                    In no event will MayaMaya be held liable or responsible for
                    any use, misuse, conduct, or action of your Analytics and/or
                    your Profile by any third-party, whether such third-party is
                    known by you or not to have access to your Analytics and/or
                    Profile. Any disclosures of your Analytics or your Profile
                    shall be made at your own risk.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="ip" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("ip")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["ip"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                7. About Intellectual Property Rights
              </h2>
            </button>
            {expandedSections["ip"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>7.1 Your Profile Ownership</h3>
                  <p>You are the sole owner of all your Profile.</p>
                </div>

                <div className="legal-subsection">
                  <h3>7.2 Company License to Profile</h3>
                  <p>
                    You hereby grant the Company and our affiliates the
                    following non-exclusive, non-transferable, royalty-free,
                    irrevocable, and perpetual license with respect to your
                    Profile the right to make, use, copy, reproduce, modify,
                    distribute, publish, display, process, prepare derivative
                    works from, and otherwise use your Profile for any purpose
                    whatsoever, without any further consent, notice, and/or
                    compensation to you or others, subject to applicable law and
                    our Privacy Policy.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>7.3 Your Feedback Rights</h3>
                  <p>
                    If you provide feedback, suggestions, and related materials
                    concerning MayaMaya, including identifying potential errors,
                    improvements, modifications, bug fixes, or enhancements
                    ("your Feedback") to the Company, you hereby grant to the
                    Company a non-exclusive, worldwide, transferable, perpetual,
                    royalty-free, and sublicensable right to make, use, copy,
                    reproduce, modify, distribute, publish, display, process,
                    prepare derivative works from, and otherwise use your
                    Feedback for any purpose whatsoever, without any further
                    consent, notice, and/or compensation to you. You agree that
                    your Feedback is gratuitous, unsolicited, and without
                    restriction, and will not place the Company under any
                    fiduciary, confidentiality, or other obligation, and that we
                    are free to disclose your Feedback on a non-confidential
                    basis or otherwise to anyone.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>7.4 Company Ownership</h3>
                  <p>
                    You understand and acknowledge that the Company owns all
                    right, title, and interest to MayaMaya and all proprietary
                    rights associated therewith. The Company reserves all rights
                    not specifically granted herein. You shall not modify any
                    copyright notices, proprietary legends, any trademark and
                    service mark attributions, any patent markings, or other
                    indication of ownership on the materials accessed through
                    MayaMaya. Any use of materials or descriptions, any
                    derivative use of MayaMaya or its contents, and any use of
                    data mining, robots, or similar data gathering and
                    extraction tools is strictly prohibited.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>7.5 Content Ownership</h3>
                  <p>
                    You acknowledge that any underlying technology used in
                    connection with MayaMaya, and all contents, features,
                    services, and functionality software, material, information,
                    communications, text, graphics, icons, scripts, links,
                    electronic art, animations, audio, video, photos, and other
                    data and the selection, and arrangement thereof
                    (collectively, the "Content") are provided by the Company or
                    third-party providers and are the intellectual property of
                    the Company and/or such third parties. Content is owned or
                    licensed by the Company and/or third parties and is
                    protected under copyright, trademark, and other proprietary
                    and intellectual property right laws. No right, title, or
                    interest in or to MayaMaya or any Content is transferred to
                    you, and all rights not expressly granted are reserved by
                    the Company. Any use of any Content not expressly permitted
                    by these Terms & Conditions is a breach of these Terms &
                    Conditions and may violate copyright, trademark, and other
                    laws.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>7.6 Limited License</h3>
                  <p>
                    The Company grants you a limited license that is
                    non-exclusive and not transferable, to access, and use the
                    Company's Content for your personal and private use only.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>7.7 Content Usage Restrictions</h3>
                  <p>
                    You may not copy, reproduce, publish, distribute, modify,
                    create derivative works of, rent, lease, sell, transfer,
                    display, broadcast, transmit, compile or collect in a
                    database, or in any manner commercially exploit any part of
                    MayaMaya or the Company's or third party's Content, in whole
                    or in part. You may not store any portion of any Content
                    owned by, or licensed to the Company in any form, whether
                    archival files, computer-readable files, or any other
                    medium. You also may not "mirror" MayaMaya or any Content on
                    any other server.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>7.8 License Termination</h3>
                  <p>
                    If you print, copy, modify, download, or otherwise use or
                    provide any other person with access to any part of MayaMaya
                    and/or Content, your right to use MayaMaya will stop
                    immediately and you must, at our option, return or destroy
                    any copies of the materials you have made.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>7.9 Trademarks</h3>
                  <p>
                    The Company name, the term MayaMaya, the Company logo, and
                    all related names, logos, domain names, product and service
                    names, designs, and slogans are trademarks of the Company.
                    You must not use such marks without the prior written
                    permission of the Company. All other names, logos, product
                    and service names, designs, and slogans on MayaMaya are the
                    trademarks of their respective owners.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="counselors" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("counselors")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["counselors"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                8. Messages to MayaMaya Counselors
              </h2>
            </button>
            {expandedSections["counselors"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>8.1 Premium Counselor Service</h3>
                  <p>
                    MayaMaya may offer a premium service where you are able to
                    speak to a career or education counselor. Communications to
                    MayaMaya Counselors must be solely made through MayaMaya.
                    The Company will not be responsible or liable for any
                    communications made through any other media such as, but not
                    limited to, e-mail, telephone, a messaging app, or some
                    other platform.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>8.2 Communication Storage</h3>
                  <p>
                    You agree that we may store any and all communications
                    between you and a MayaMaya counselor. You further
                    acknowledge and agree that we may store communications
                    between you and us for as long as there is a practical
                    purpose for use, and we may use such communications for data
                    analytics purposes, improve our products, and/or develop a
                    database of our users.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>8.3 Disclosure for Legal Purposes</h3>
                  <p>
                    You acknowledge and agree that we may disclose any and all
                    communications between you and a MayaMaya counselor for any
                    law enforcement investigations or law enforcement purposes
                    without any notice to you.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>8.4 Content Standards with Counselors</h3>
                  <p>
                    You agree to abide by MayaMaya's Content Standards when
                    communicating with our Counselors. When communicating with
                    our Counselors, you will not:
                  </p>
                  <ul className="legal-content-list">
                    <li>
                      Convey any material that is defamatory, racist, sexist,
                      obscene, indecent, abusive, offensive, harassing, violent,
                      hateful, inflammatory, or otherwise objectionable
                    </li>
                    <li>
                      Convey any sexually explicit or pornographic material,
                      violence
                    </li>
                    <li>Make commercial sales or pitches</li>
                  </ul>
                </div>

                <div className="legal-subsection">
                  <h3>8.5 Communication Restrictions</h3>
                  <p>
                    You acknowledge that we have a right to limit or restrict
                    any communications with a MayaMaya counselor for actions,
                    communications, or behavior that we reasonably believe could
                    intimidate, threaten, harm, or have a negative effect on our
                    Counselors.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="reliance" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("reliance")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["reliance"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                9. Reliance on MayaMaya's Assessments and Recommendations
              </h2>
            </button>
            {expandedSections["reliance"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>9.1 Assessment Disclaimer</h3>
                  <p>
                    All profile assessments are solely our opinions. We do not
                    warrant the accuracy, completeness, or usefulness of this
                    information. Any reliance you place on such information is
                    strictly at your own risk. We disclaim all liability and
                    responsibility arising from any reliance placed on our
                    assessments or recommendations by you or any other MayaMaya
                    user, or by anyone who may be informed of any of its
                    contents.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>9.2 Third-Party Sites and Links</h3>
                  <p>
                    MayaMaya may contain links through advertising and otherwise
                    to third party websites, apps, or platforms, and other
                    resources (collectively "Third Party Sites"). These links
                    are made to Third Party Sites that are not under the control
                    of, reviewed, monitored, or endorsed by the Company, and as
                    such the Company is not responsible or liable for any
                    information, content, or any other materials of any linked
                    website. Such links are provided for convenience and/or
                    entertainment purposes only.
                  </p>
                  <p>
                    You agree that you are solely responsible and liable for any
                    interactions you may have with such linked Third Party
                    Sites. You acknowledge and agree that the Company shall not
                    be responsible or liable in any way, both directly or
                    indirectly, for any damage or loss whatsoever caused, or
                    alleged to be caused by or in connection with the use of or
                    reliance upon any such third-party goods or services, or
                    content, available through any Third Party Sites.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="privacy" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("privacy")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["privacy"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">10. Your Privacy</h2>
            </button>
            {expandedSections["privacy"] && (
              <div className="legal-section-content">
                <p>
                  Please review MayaMaya's Privacy Policy, which are considered
                  part of, and are incorporated into, these Terms & Conditions.
                  By accepting these Terms & Conditions, you accept our Privacy
                  Policy.
                </p>
              </div>
            )}
          </section>

          <section id="liability" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("liability")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["liability"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">
                11. Our Liability, Disclaimer Of Warranty, And Limitations Of
                Liability
              </h2>
            </button>
            {expandedSections["liability"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>11.1 MayaMaya Recommendations</h3>
                  <p>
                    MayaMaya uses proprietary methodology to qualitatively and
                    quantitatively assess your education and current
                    professional level, and your education and career goals.
                    While we make a bona fide attempt to provide you with our
                    recommendations (e.g. suggestions, courses, education, and
                    career advice uniquely suitable for you), we cannot
                    guarantee that our recommendations are suitable for you to
                    any degree. Therefore, the Company disclaims any and all
                    liabilities for any loss, damage, or injury based on the
                    accuracy, completeness, or suitability of any suggestions
                    provided through MayaMaya.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>11.2 Viruses and Data Accuracy</h3>
                  <p>
                    You understand that we cannot and do not guarantee or
                    warrant MayaMaya will be free of viruses or other
                    destructive code. You are responsible for implementing
                    sufficient procedures and checkpoints to satisfy your
                    particular requirements for anti-virus protection and
                    accuracy of data input and output, and for maintaining a
                    means external to our site for any reconstruction of any
                    lost data.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>11.3 Disclaimer of Warranty</h3>
                  <p>
                    MAYAMAYA IS PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS
                    WITHOUT REPRESENTATIONS OR WARRANTIES OF ANY KIND, EITHER
                    EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PROVIDED BY LAW,
                    THE COMPANY HEREBY DISCLAIMS ALL WARRANTIES OF ANY KIND,
                    WHETHER EXPRESS OR IMPLIED, STATUTORY, OR OTHERWISE,
                    INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF
                    MERCHANTABILITY, NON-INFRINGEMENT, AND FITNESS FOR
                    PARTICULAR PURPOSE. IN NO EVENT WILL THE COMPANY OR ITS
                    DIRECTORS, OFFICERS, EMPLOYEES, AFFILIATES, OR AGENTS BE
                    LIABLE TO YOU OR ANY THIRD PARTY FOR ANY INCIDENTAL,
                    INDIRECT, SPECIAL, OR CONSEQUENTIAL DAMAGES (EVEN IF
                    MAYAMAYA HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
                    DAMAGES), ARISING OUT OF YOUR USE OF OR INABILITY TO ACCESS
                    MAYAMAYA OR USE MAYAMAYA, INCLUDING WITHOUT LIMITATION, LOSS
                    OF REVENUE OR ANTICIPATED PROFITS, LOSS OF GOODWILL, LOST
                    BUSINESS, LOST DATA, COMPUTER FAILURE OR MALFUNCTION, OR ANY
                    AND ALL OTHER DAMAGES OR LOSSES THAT RESULT FROM MISTAKES,
                    OMISSIONS, INTERRUPTIONS, DELETIONS OF FILES, ERRORS,
                    DEFECTS, VIRUSES, DELAYS IN OPERATION OR TRANSMISSION, OR
                    ANY FAILURE OF PERFORMANCE, WHETHER OR NOT LIMITED TO ACTS
                    OF GOD, COMMUNICATIONS FAILURE, THEFT, DESTRUCTION, OR
                    UNAUTHORIZED ACCESS TO MAYAMAYA’S RECORDS, PROGRAMS, OR
                    SERVICES. IF YOU ARE DISSATISFIED WITH ANY PORTION OF
                    MAYAMAYA, OR WITH ANY OF THESE TERMS & CONDITIONS, YOUR SOLE
                    AND EXCLUSIVE REMEDY IS TO DISCONTINUE USING MAYAMAYA.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>11.4 DDoS and Harmful Material</h3>
                  <p>
                    TO THE FULLEST EXTENT PROVIDED BY LAW, WE WILL NOT BE LIABLE
                    FOR ANY LOSS OR DAMAGE CAUSED BY A DISTRIBUTED
                    DENIAL-OF-SERVICE ATTACK, VIRUSES, OR OTHER TECHNOLOGICALLY
                    HARMFUL MATERIAL THAT MAY INFECT YOUR COMPUTER EQUIPMENT,
                    COMPUTER PROGRAMS, DATA, OR OTHER PROPRIETARY MATERIAL DUE
                    TO YOUR USE OF THE SITE OR ANY SERVICES OR ITEMS OBTAINED
                    THROUGH THE SITE OR TO YOUR DOWNLOADING OF ANY MATERIAL
                    POSTED ON IT, OR ON ANY SITE LINKED TO IT.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>11.5 Use at Your Own Risk</h3>
                  <p>
                    YOUR USE OF MAYAMAYA, INCLUDING ITS CONTENT, AND ANY
                    SERVICES OR ITEMS OBTAINED THROUGH MAYAMAYA IS AT YOUR OWN
                    RISK. MAYAMAYA, INCLUDING ITS CONTENT, AND ANY SERVICES OR
                    ITEMS OBTAINED THROUGH MAYAMAYA ARE PROVIDED ON AN "AS IS"
                    AND "AS AVAILABLE" BASIS, WITHOUT ANY WARRANTIES OF ANY
                    KIND, EITHER EXPRESS OR IMPLIED. NEITHER THE COMPANY NOR ANY
                    PERSON ASSOCIATED WITH THE COMPANY MAKES ANY WARRANTY OR
                    REPRESENTATION WITH RESPECT TO THE COMPLETENESS, SECURITY,
                    RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF THE SITE.
                    WITHOUT LIMITING THE FOREGOING, NEITHER THE COMPANY NOR
                    ANYONE ASSOCIATED WITH THE COMPANY REPRESENTS OR WARRANTS
                    THAT MAYAMAYA, INCLUDING ITS CONTENT, OR ANY SERVICES OR
                    ITEMS OBTAINED THROUGH MAYAMAYA, WILL BE ACCURATE, RELIABLE,
                    ERROR-FREE, OR UNINTERRUPTED, THAT DEFECTS WILL BE
                    CORRECTED, THAT MAYAMAYA IS FREE OF VIRUSES OR OTHER HARMFUL
                    COMPONENTS, OR THAT MAYAMAYA MEETS YOUR NEEDS OR
                    EXPECTATIONS.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>11.6 Limitation of Liability</h3>
                  <p>
                    TO THE FULLEST EXTENT PROVIDED BY LAW, IN NO EVENT WILL THE
                    COMPANY, ITS SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS,
                    OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY
                    LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE,
                    OR INABILITY TO USE, MAYAMAYA, INCLUDING ANY CONTENT ON
                    MAYAMAYA, INCLUDING ANY DIRECT, INDIRECT, SPECIAL,
                    INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
                    BUT NOT LIMITED TO, PERSONAL INJURY, PAIN AND SUFFERING,
                    EMOTIONAL DISTRESS, LOSS OF REVENUE, LOSS OF PROFITS, LOSS
                    OF BUSINESS OR ANTICIPATED SAVINGS, LOSS OF USE, LOSS OF
                    GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY TORT
                    (INCLUDING NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE,
                    EVEN IF FORESEEABLE.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>11.7 Jurisdictional Limitations</h3>
                  <p>
                    BECAUSE SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR
                    LIMITATION OF LIABILITY FOR CONSEQUENTIAL OR INCIDENTAL
                    DAMAGES, IN SUCH STATES MAYAMAYA’S LIABILITY IS LIMITED TO
                    THE GREATEST EXTENT PERMITTED BY LAW.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>11.8 Time To File Claims</h3>
                  <p>
                    ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE ARISING OUT OF OR
                    RELATING TO THESE TERMS & CONDITIONS OR THE WEBSITE MUST BE
                    COMMENCED WITHIN THREE (3) MONTHS AFTER THE CAUSE OF ACTION
                    ACCRUES; OTHERWISE, SUCH CAUSE OF ACTION OR CLAIM IS
                    PERMANENTLY BARRED.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="indemnification" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("indemnification")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["indemnification"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">12. Indemnification</h2>
            </button>
            {expandedSections["indemnification"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>12.1 Indemnification</h3>
                  <p>
                    You agree to defend, indemnify, and hold harmless the
                    Company and its service providers, and its and their
                    respective officers, directors, employees, contractors,
                    agents, licensors, suppliers, successors, and assigns from
                    and against any claims, liabilities, damages, judgments,
                    awards, losses, costs, expenses, or fees (including
                    reasonable attorneys' fees) arising out of or relating to
                    (i) your violation of these Terms & Conditions, (ii) your
                    use of MayaMaya, (iii) your use of any information obtained
                    from MayaMaya, or (iv) any claim of defamation, invasion of
                    privacy, right of publicity, infringement of intellectual
                    property or other proprietary rights relating to your use of
                    MayaMaya.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section id="miscellaneous" className="legal-section">
            <button
              className="legal-section-toggle"
              onClick={() => toggleSection("miscellaneous")}
            >
              <span className="legal-toggle-icon">
                {expandedSections["miscellaneous"] ? "−" : "+"}
              </span>
              <h2 className="legal-section-title">13. Miscellaneous</h2>
            </button>
            {expandedSections["miscellaneous"] && (
              <div className="legal-section-content">
                <div className="legal-subsection">
                  <h3>13.1 Governing Law and Jurisdiction</h3>
                  <p>
                    This Terms & Conditions shall be governed by and construed
                    in accordance with the laws of the State of New York, U.S.A.
                    applicable to agreements executed and fully performed
                    therein. It is agreed that New York courts (state and
                    federal) only, will have jurisdiction over any controversies
                    regarding this Agreement; any action or proceeding which
                    involves such a controversy will be brought only in those
                    courts the federal Southern District of New York or in
                    Queens County, New York.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>13.2 Severability</h3>
                  <p>
                    If any provision of this Agreement is determined by any
                    court of competent jurisdiction to be unenforceable, such
                    provision shall be interpreted to have the broadest
                    application as shall be enforceable. The unenforceability of
                    any particular provision of this Agreement shall not affect
                    any other provision hereof, which shall continue in full
                    force and effect.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>13.3 No Waiver</h3>
                  <p>
                    No waiver by the Company of any term or condition set out in
                    these Terms & Conditions shall be deemed a further or
                    continuing waiver of such term or condition or a waiver of
                    any other term or condition, and any failure of the Company
                    to assert a right or provision under these Terms &
                    Conditions shall not constitute a waiver of such right or
                    provision.
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>13.4 Communications</h3>
                  <p>
                    Unless otherwise stated, all feedback, comments, requests
                    for technical support, and other communications relating to
                    MayaMaya should be submitted through{" "}
                    <a href={MAYAMAYA_PROD_WHITELABEL_SUPPORT_URL}>
                      MayaMaya Support
                    </a>
                    .
                  </p>
                </div>

                <div className="legal-subsection">
                  <h3>13.5 Entire Agreement</h3>
                  <p>
                    The Terms & Conditions and our Privacy Policy constitute the
                    sole and entire agreement between you and the Company
                    regarding MayaMaya and supersede all prior and
                    contemporaneous understandings, agreements, representations,
                    and warranties, both written and oral, regarding MayaMaya.
                  </p>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
